import React from 'react';
import { Shield, Zap, Users, Award, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import Footer from '../components/footer/index';
import { useDarkMode } from '../components/darkmode/DarkModeContext';

const AboutPage = () => {
  const { isDarkMode } = useDarkMode();

  const companyValues = [
    { icon: <Award className="w-8 h-8" />, title: "Kualitas", description: "Kami menjadikan kualitas sebagai prioritas utama dalam setiap layanan yang kami berikan, guna menghadirkan pengalaman terbaik bagi setiap pelanggan." },
    { icon: <Shield className="w-8 h-8" />, title: "Keandalan", description: "Kami berkomitmen untuk menyediakan koneksi internet yang stabil dan dapat diandalkan, menjadikan keandalan sebagai landasan operasional kami." },
    { icon: <Zap className="w-8 h-8" />, title: "Inovasi", description: "Dengan semangat inovasi yang berkelanjutan, kami secara proaktif mengeksplorasi pendekatan baru untuk meningkatkan kualitas layanan." },
    { icon: <Users className="w-8 h-8" />, title: "Pelayanan Pelanggan", description: "Kami menyediakan layanan pelanggan yang ramah, responsif, dan profesional, siap membantu pelanggan dalam setiap kebutuhan." }
  ];

  const stats = [
    { number: "150+", description: "Proyek Aktif" },
    { number: "100+", description: "Tim Ahli" },
    { number: "200+", description: "Klien Kami" },
    { number: "30", description: "Sertifikat" }
  ];

  const missionPoints = [
    "Providing broadband internet and dedicated internet connections",
    "Delivering comprehensive network infrastructure solutions",
    "Building strategic partnerships for mutual growth",
    "Supporting digital infrastructure development in Indonesia",
    "Building Stronger Communities"
  ];

  return (
    <div
      className="min-h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      {/* Breadcrumb */}
      <div className={`pt-20 py-3 px-6 ${isDarkMode ? 'bg-gray-800/50' : 'bg-[#DFE4E0]'} transition-colors`}>
        <div className="max-w-7xl mx-auto">
          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
            <span>Home</span> / <span className="text-[#1E9B4C]">About</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className={`text-5xl lg:text-6xl font-bold mb-6 leading-tight ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
              Crafting Excellence
              <span className="block text-[#1E9B4C]">Together</span>
            </h1>
            <p className={`text-lg mb-8 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Sejak 2021, kami telah berkomitmen memberikan layanan internet berkualitas tinggi dengan teknologi terdepan...
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className={`text-3xl font-bold mb-1 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>{s.number}</div>
                  <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`}>{s.description}</div>
                </div>
              ))}
            </div>

            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm leading-relaxed`}>
              At Telnet Indonesia, we are committed to revolutionizing the internet service industry...
            </p>
          </div>

          <div className="relative">
            <div className="relative">
              <img
                src="image/banner1.png"
                alt="Team collaboration"
                className="rounded-2xl shadow-lg w-full"
              />
              <div className={`absolute top-4 right-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} p-4 rounded-full shadow-lg`}>
                <div className="w-12 h-12 bg-[#CDEBD8] rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#1E9B4C]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img
              src="image/banner2.png"
              alt="Network infrastructure"
              className="rounded-xl"
            />
            <img
              src="image/banner3.png"
              alt="Fiber optic cables"
              className="rounded-xl mt-8"
            />
          </div>

          <div>
            <h2 className={`text-4xl font-bold mb-6 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>Our Mission</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg mb-8 leading-relaxed`}>
              To provide exceptional internet services and deliver best-in-class connectivity...
            </p>

            <ul className="space-y-4">
              {missionPoints.map((p, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#1E9B4C] flex-shrink-0" />
                  <span className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className={`text-4xl font-bold mb-6 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>Our Vision</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg mb-8 leading-relaxed`}>
              To become Indonesia's leading internet service provider through innovative solutions...
            </p>

            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#1E9B4C]" />
                <span className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Inspiring Modern Architecture</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#1E9B4C]" />
                <span className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Network Infrastructure Excellence</span>
              </li>
            </ul>
          </div>

          <div>
            <img
              src="image/banner4.png"
              alt="Team working"
              className="rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>Nilai-Nilai Perusahaan</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg max-w-3xl mx-auto`}>
              Nilai-nilai yang menjadi fondasi dalam setiap langkah dan keputusan yang kami ambil
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((v, i) => (
              <div
                key={i}
                className={`rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow border
                  ${isDarkMode ? 'bg-gray-800 border-gray-700 text-green-50' : 'bg-white border-gray-200 text-gray-900'}`}
              >
                <div className="text-[#1E9B4C] mb-4">{v.icon}</div>
                <h3 className="text-xl font-bold mb-3">{v.title}</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed text-sm`}>{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Company */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className={`text-4xl font-bold mb-6 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>Tentang Perusahaan</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg mb-6 leading-relaxed`}>
              PT Telekomunikasi Internet Indonesia (Telnet Indonesia) didirikan pada Februari 2021...
            </p>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-8 leading-relaxed`}>
              Dengan infrastruktur jaringan yang handal dan tim support teknis berpengalaman...
            </p>

            <div className="space-y-6">
              <div>
                <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>Telnet Home</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                  Layanan internet rumah dengan jaringan fiber optik...
                </p>
              </div>
              <div>
                <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>Telnet Fiber</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                  Pengelolaan infrastruktur jaringan telekomunikasi khusus fiber optik...
                </p>
              </div>
            </div>
          </div>

         <div>
        <img
          src="image/banner5.png"
          alt="Office building"
          className="rounded-2xl shadow-lg"
          style={{ width: '1000px', height: 'auto' }} // Ukuran bisa disesuaikan di sini
        />
      </div>

        </div>
      </section>

      {/* Contact */}
      <section className={`${isDarkMode ? 'bg-gray-900' : ''} py-20 px-6`}
      style={!isDarkMode ? { backgroundColor: "#152E50" } : {}}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-12">Hubungi Kami</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="w-16 h-16 bg-[#1E9B4C] rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">Alamat Kantor</h3>
              <p className="text-gray-300">Jl. Dr. Mr. Mohd. Hasan No. 33<br />Kota Banda Aceh, 23245</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#1E9B4C] rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">Telepon</h3>
              <p className="text-gray-300">08777 888 2022</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#1E9B4C] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">Email</h3>
              <p className="text-gray-300">info@telnet.co.id</p>
            </div>
          </div>
          <div className="mt-12">
            <a
              href="https://www.telnet.co.id"
              className="inline-block bg-[#1E9B4C] text-white px-8 py-4 rounded-md font-bold hover:bg-[#188A42] transition-colors"
            >
              Kunjungi Website Kami
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
